/** Extracts transport metadata without leaking the transport error into feature UI. */
const requestFailure = (error: unknown) => {
  if (!(error instanceof Error)) return { message: String(error) };
  return {
    message: error.message,
    status: numberProperty(error, 'status'),
    code: stringProperty(error, 'code'),
    retryAfterSeconds: numberProperty(error, 'retryAfterSeconds'),
  };
};

/** Feature-facing error that preserves useful transport metadata without
 * exposing the transport error class to UI consumers. The name is an explicit
 * literal here and on every subclass so error-tracking titles and grouping
 * fingerprints survive minification - reading it off the constructor
 * (new.target.name) mangles to a single letter in the production build. */
export class SemanticRequestError extends Error {
  readonly name: string = 'SemanticRequestError';
  readonly status?: number;
  readonly code?: string;
  readonly retryAfterSeconds?: number;

  constructor(error: unknown) {
    const failure = requestFailure(error);
    super(failure.message, { cause: error });
    this.status = failure.status;
    this.code = failure.code;
    this.retryAfterSeconds = failure.retryAfterSeconds;
  }
}

type SemanticErrorConstructor = new (error: unknown) => SemanticRequestError;

export const semanticRequest = async <T>(
  request: () => Promise<unknown>,
  reader: (value: unknown) => T,
  ErrorType: SemanticErrorConstructor = SemanticRequestError,
): Promise<T> => {
  try {
    return reader(await request());
  } catch (error) {
    if (error instanceof ErrorType) throw error;
    throw new ErrorType(error);
  }
};

export const requestHasStatus = (error: unknown, ...statuses: number[]): boolean =>
  error instanceof SemanticRequestError &&
  statuses.includes(error.status ?? Number.NaN);

const numberProperty = (error: Error, key: string): number | undefined => {
  const value = (error as unknown as Record<string, unknown>)[key];
  return typeof value === 'number' ? value : undefined;
};

const stringProperty = (error: Error, key: string): string | undefined => {
  const value = (error as unknown as Record<string, unknown>)[key];
  return typeof value === 'string' ? value : undefined;
};
